import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { centralizedHost } from "~/Utils";
import { removeSasFromDescription } from "../../Imagehelper";
import { fields } from "../constants";
import { usePrivacyPolicyTabOnePara } from "../TabOneParagraph/TabOnePara.hooks";

const AddEditTabOnePara = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();

  const {
    data: editData,
    error,
    isLoading: isLoadingEdit,
    isError,
    refetch: refetchEdit,
  } = usePrivacyPolicyTabOnePara(query.get("editId") || "");

  const {
    data: editDataById,
    isLoading: isLoadingDocument,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchEdit();
    }
  }, [query.get("editId"), query.get("id")]);

  const formConfig: FormConfig = {
    initialValues: {
      title: decodeHTML(editDataById?.payload?.title) || decodeHTML(editData?.title) || "",
      description: decodeHTML(editDataById?.payload?.description) || decodeHTML(editData?.description) || "",

      parent_id_1: editDataById
        ? decodeHTML(editDataById.payload?.parent_id_1)
        : editData
          ? decodeHTML(editData?.parent_id_1)
          : null,

      published: editDataById?.payload?.published ?? editData?.published ?? false,
    },
    fields: fields.privacyPolicyTabOne,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let descriptionKeys: any = ["description"];
    for (const key of descriptionKeys) {
      if (data[key]) {
        data[key] = removeSasFromDescription(data[key]);
      }
    }

    const payload = {
      action: action,
      method: method,
      module: "Centralized Policies",
      subModule: "privacyPolicyTabOne",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "CENTRALIZED",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "CENTRALIZED_privacyPolicyTabOne",

      payload: {
        ...(action === "UPDATE" && { id: editData?.id || editDataById?.payload?.id }),
        ...data,
        created_by: action === "UPDATE" ? editData?.created_by || editDataById?.payload?.created_by : undefined,
        last_modified_by: action === "UPDATE" ? user?.email : null,
      },
      status: status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Tab Paragraph`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingEdit || isLoadingDocument}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            query.get("editId")
              ? `${centralizedHost}/api/privacy-policy-list/${editData?.id}`
              : `${centralizedHost}/api/privacy-policy-list`,
            query.get("editId") ? "UPDATE" : "CREATE",
            query.get("editId") ? "PATCH" : "POST",
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Policies"
      />
    </ContentWrapper>
  );
};

export default AddEditTabOnePara;
